import Box from '@/common/components/Box';
import Button from '@/common/components/Button';
import Checkbox from '@/common/components/Checkbox';
import GoBackButton from '@/common/components/GoBackButton';
import TextArea from '@/common/components/TextArea';
import TextField from '@/common/components/TextField';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronsRight, UserRoundIcon, Calendar, Clock } from 'lucide-react';
import { useTmaisBanner } from '@/features/tmaisBanners/hooks/useTmaisBanner';
import { extractDateTime } from '@/common/utils/dateUtils';

const TmaisBannerShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: banner,
    isPending,
    error = {} as any,
    refetch,
  } = useTmaisBanner({ id });

  const hasExpiration = !!banner?.expires_at;

  return (
    <ErrorMessageWrapper statusCode={error?.status} refetchFunction={refetch}>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/tmais-banners" searchLastRouteFromFallback />
            <p className="text-gray-400 font-normal">Banner</p>
            <ChevronsRight className="text-gray-400 w-4 h-4" />
            <h2 className="text-white font-semibold">Detalhes do Banner</h2>
          </div>

          <div className="flex gap-4">
            <Button
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={isPending}
              onClick={() => navigate(`/tmais-banners/${id}/edit`)}
              operationKlassToCheckPermission="Admin::Tmais::Banners::Update"
            >
              Editar
            </Button>
          </div>
        </div>

        <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
          <p className="text-xl font-bold">Detalhes do Banner</p>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-400">Autor</label>
            <div className="flex items-center gap-2 p-4 w-full h-[52px] rounded-md bg-neutral-op-8 border border-transparent">
              <ImageWithPlaceholder
                url={(banner?.creator as any)?.profile_picture?.url}
                alt={banner?.creator?.full_name || 'Autor'}
                className="size-6 rounded-full shrink-0 overflow-hidden"
                iconClassName="size-4"
                placeholderIcon={UserRoundIcon}
              />
              <span className="text-sm text-white font-medium">
                {banner?.creator?.full_name || (banner as any)?.author?.full_name || '--'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Data de envio"
              value={extractDateTime(banner?.active_at, 'date')}
              disabled
              LeftIcon={<Calendar className="w-5 h-5 text-gray-400" />}
            />
            <TextField
              label="Hora para envio"
              value={extractDateTime(banner?.active_at, 'time')}
              disabled
              LeftIcon={<Clock className="w-5 h-5 text-gray-400" />}
            />
          </div>

          <TextField
            label="Redirecionar clique para"
            value={banner?.mobile_redirect_to || '--'}
            disabled
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch mb-2">
            <div className="flex flex-col w-full h-full min-h-[220px]">
              <div className="relative flex-1 w-full h-full bg-neutral-op-8 border border-transparent rounded-md overflow-hidden flex items-center justify-center">
                {(banner as any)?.file?.url ? (
                  <img
                    src={(banner as any)?.file?.url}
                    alt="Preview do banner"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-gray-400 text-sm relative z-10">Nenhuma imagem cadastrada</span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-4 h-full">
              <TextField
                label="Nome do banner"
                value={banner?.title || '--'}
                disabled
              />

              <TextArea
                label="Descrição interna (aparece quando hover)"
                value={banner?.description || '--'}
                disabled
                className="flex-1 h-full resize-none"
                rows={5}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 mt-2">
            <Checkbox
              label="Adicionar data de expiração"
              className="text-gray-300"
              checked={hasExpiration}
              disabled
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                label="Data de expiração"
                value={hasExpiration ? extractDateTime(banner?.expires_at, 'date') : '--'}
                disabled
                LeftIcon={<Calendar className="w-5 h-5 text-gray-400" />}
              />
              <TextField
                label="Hora para expiração"
                value={hasExpiration ? extractDateTime(banner?.expires_at, 'time') : '--'}
                disabled
                LeftIcon={<Clock className="w-5 h-5 text-gray-400" />}
              />
            </div>
          </div>
        </Box>
      </div>
    </ErrorMessageWrapper>
  );
};

export default TmaisBannerShow;
